/**
 * Техника (AK-235) — TanStack Query hooks for the machinery catalog.
 *
 * Real-backend only (no mock toggle — backend endpoints land in this sprint's
 * migration 048 + reference.py machinery section). Pattern mirrors
 * useCatalogQueries.ts (AK-232/233/234 diseases/pests).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { usePagedInfinite } from '@/pages/platform-admin/reference/hooks/usePagedInfinite';
import type { PagedResponse } from '@/pages/platform-admin/reference/types';
import type {
  CompatibilityPayload,
  MachineryGroupsEnvelope,
  MachineryModelDetail,
  MachineryModelListItem,
  MachineryModelListQuery,
  MachineryModelPayload,
  MachineryType,
  MachineryTypePayload,
  MachineryTypesEnvelope,
  Manufacturer,
  ManufacturerPayload,
  ManufacturersEnvelope,
} from '@/pages/platform-admin/reference/machineryCatalogTypes';

export const machineryKeys = {
  groups: () => ['reference', 'machinery-groups'] as const,
  types: (group?: string) => ['reference', 'machinery-types', group ?? 'all'] as const,
  manufacturers: () => ['reference', 'manufacturers'] as const,
  models: (q: MachineryModelListQuery) => ['reference', 'machinery-models', q] as const,
  model: (id: string) => ['reference', 'machinery-models', 'detail', id] as const,
};

function buildModelParams(q: MachineryModelListQuery): string {
  const params = new URLSearchParams();
  if (q.search) params.set('search', q.search);
  if (q.machinery_group && q.machinery_group !== 'all') params.set('machinery_group', q.machinery_group);
  if (q.machinery_type_id && q.machinery_type_id !== 'all') params.set('machinery_type_id', q.machinery_type_id);
  if (q.manufacturer_id && q.manufacturer_id !== 'all') params.set('manufacturer_id', q.manufacturer_id);
  params.set('limit', String(q.limit ?? 200));
  params.set('offset', String(q.offset ?? 0));
  return params.toString();
}

// ---------- Groups ----------

export function useMachineryGroups(): UseQueryResult<MachineryGroupsEnvelope, Error> {
  return useQuery({
    queryKey: machineryKeys.groups(),
    queryFn: () => api.get<MachineryGroupsEnvelope>('/api/v1/reference/machinery-groups'),
    staleTime: 5 * 60_000,
  });
}

// ---------- Types ----------

export function useMachineryTypes(
  group?: string,
): UseQueryResult<MachineryTypesEnvelope, Error> {
  return useQuery({
    queryKey: machineryKeys.types(group),
    queryFn: () => {
      const qs = group && group !== 'all' ? `?machinery_group=${group}` : '';
      return api.get<MachineryTypesEnvelope>(`/api/v1/reference/machinery-types${qs}`);
    },
    staleTime: 60_000,
  });
}

export function useCreateMachineryType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: MachineryTypePayload) =>
      api.post<MachineryType>('/api/v1/reference/machinery-types', p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reference', 'machinery-types'] }),
  });
}

export function useUpdateMachineryType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: MachineryTypePayload }) =>
      api.patch<MachineryType>(`/api/v1/reference/machinery-types/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reference', 'machinery-types'] }),
  });
}

export function useDeleteMachineryType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`/api/v1/reference/machinery-types/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reference', 'machinery-types'] }),
  });
}

// ---------- Manufacturers ----------

export function useManufacturers(): UseQueryResult<ManufacturersEnvelope, Error> {
  return useQuery({
    queryKey: machineryKeys.manufacturers(),
    queryFn: () => api.get<ManufacturersEnvelope>('/api/v1/reference/manufacturers'),
    staleTime: 60_000,
  });
}

export function useCreateManufacturer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: ManufacturerPayload) =>
      api.post<Manufacturer>('/api/v1/reference/manufacturers', p),
    onSuccess: () => qc.invalidateQueries({ queryKey: machineryKeys.manufacturers() }),
  });
}

export function useUpdateManufacturer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ManufacturerPayload }) =>
      api.patch<Manufacturer>(`/api/v1/reference/manufacturers/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: machineryKeys.manufacturers() }),
  });
}

export function useDeleteManufacturer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`/api/v1/reference/manufacturers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: machineryKeys.manufacturers() }),
  });
}

// ---------- Models ----------

export function useMachineryModels(
  query: MachineryModelListQuery,
  options?: { enabled?: boolean },
): UseQueryResult<PagedResponse<MachineryModelListItem>, Error> {
  return useQuery({
    queryKey: machineryKeys.models(query),
    queryFn: () =>
      api.get<PagedResponse<MachineryModelListItem>>(
        `/api/v1/reference/machinery-models?${buildModelParams(query)}`,
      ),
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Бесконечный список моделей техники (AK-574). Раньше экран брал первые 200 и
 * рисовал рядом честный `total` — обрыв был не виден.
 */
export function useMachineryModelsInfinite(query: MachineryModelListQuery) {
  const { limit: _limit, offset: _offset, ...filter } = query;
  return usePagedInfinite<MachineryModelListItem>(
    ['reference', 'machinery-models', 'infinite', filter] as const,
    (offset, limit) =>
      `/api/v1/reference/machinery-models?${buildModelParams({
        ...filter,
        limit,
        offset,
      })}`,
  );
}

export function useMachineryModel(
  id: string | null,
): UseQueryResult<MachineryModelDetail, Error> {
  return useQuery({
    queryKey: machineryKeys.model(id ?? ''),
    queryFn: () =>
      api.get<MachineryModelDetail>(`/api/v1/reference/machinery-models/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateMachineryModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: MachineryModelPayload) =>
      api.post<MachineryModelDetail>('/api/v1/reference/machinery-models', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reference', 'machinery-models'] });
      qc.invalidateQueries({ queryKey: ['reference', 'machinery-types'] });
      qc.invalidateQueries({ queryKey: machineryKeys.manufacturers() });
    },
  });
}

export function useUpdateMachineryModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: MachineryModelPayload }) =>
      api.patch<MachineryModelDetail>(`/api/v1/reference/machinery-models/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reference', 'machinery-models'] });
      qc.invalidateQueries({ queryKey: ['reference', 'machinery-types'] });
      qc.invalidateQueries({ queryKey: machineryKeys.manufacturers() });
    },
  });
}

export function useArchiveMachineryModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`/api/v1/reference/machinery-models/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reference', 'machinery-models'] }),
  });
}

export function useSetMachineryCompatibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CompatibilityPayload }) =>
      api.put<MachineryModelDetail>(
        `/api/v1/reference/machinery-models/${id}/compatibility`,
        payload,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['reference', 'machinery-models'] });
      qc.invalidateQueries({ queryKey: machineryKeys.model(variables.id) });
    },
  });
}
